import { Injectable,OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable,Subject } from 'rxjs';
import { filter,map,shareReplay,switchMap,takeUntil,tap } from 'rxjs/operators';
import { PartnerEntity,PartnerSpecificationType } from '../../dave-data-module/entities/partner.entity';
import { PartnerResolver } from '../../dave-data-module/guards/partner.resolver';
import { State } from '../../dave-data-module/State';
import { getPartner,getPartnerFetched } from '../../dave-data-module/State/selectors/partners.selectors';
import { CustomLabels, label } from './custom-labels';
import { DefaultLabels } from './default-labels';
import { deepMerge, Require } from '../../helper/helper';

const IBBAU_PARTNERID: number = window["env"]["ibbau_partner_id"];
const PDA_PARTNERID: number = window["env"]["pda_partner_id"];
const HOFF_PARTNERID: number = window["env"]["hoff_partner_id"];
const STEINHOFF_PARTNERID: number = window["env"]["steinhoff_partner_id"];
const LUKSCH_PARTNERID: number = window["env"]["luksch_partner_id"];
const LUKSCH_PARTNERID2: number = window["env"]["luksch_partner_id2"];

const GUTTHANN_PARTNERID: number = 466;
const DEMO_KONTO_PARTNERID: number = 440;


const partnersWithProjects = [IBBAU_PARTNERID, HOFF_PARTNERID, PDA_PARTNERID, STEINHOFF_PARTNERID, LUKSCH_PARTNERID, LUKSCH_PARTNERID2, GUTTHANN_PARTNERID, DEMO_KONTO_PARTNERID];
const partnersWithBauherr = [IBBAU_PARTNERID, HOFF_PARTNERID, STEINHOFF_PARTNERID, GUTTHANN_PARTNERID, DEMO_KONTO_PARTNERID];

interface customLabelsLegacy {
    CustomerSupplier: Require<label, 'wordBinding'>;
    CustomerList: label;
    Commission: Require<label, 'wordBinding'>;
    Supplier: Require<label, 'wordBinding'>;
    Customer: Require<label, 'wordBinding'>;
    Costs: Require<label, 'wordBinding'>;
}
type fallType = 'Nominativ' | 'Dativ' | 'Genitiv' | 'Akkusativ';


const staticLabels = {
    Supplier: {
        wordBinding: 'Lieferanten',
        Nominativ: {
            singular: {
                article: 'der',
                value:  'Lieferant',
            },
            plural: {
                article: 'die',
                value:  'Lieferanten',
            },
        },
        Dativ: {
            singular: {
                article:  'der',
                value:  'Lieferant',
            },
            plural: {
                article: 'den',
                value: 'Lieferanten',
            },
        },
    },
    Costs: {
        wordBinding: '',
        Nominativ: {
            singular: {
                article: 'der',
                value:  'Einkaufspreis',
            },
            plural: {
                article: 'die',
                value:  'Einkaufspreise',
            },
        },
        Dativ: {
            singular: {
                article:  'der',
                value:  'Einkaufspreis',
            },
            plural: {
                article: 'den',
                value: 'Einkaufspreise',
            },
        },
    },
}

@Injectable({
    providedIn: 'root',
})
export class CustomLabelService implements OnDestroy {
    private destroy$ = new Subject<void>();
    public readonly labels$: Observable<customLabelsLegacy&CustomLabels> = this.store.select(getPartnerFetched).pipe(
        tap((f) => {
            if (!f) {
                this.partnerResolver.resolve();
            }
        }),
        filter((f) => f),
        switchMap(() => this.store.select(getPartner)),
        filter((f) => !!f),
        takeUntil(this.destroy$),
        map((partner) => {

            const partnerCustomLabels: CustomLabels = partner.CustomLabels || {};
            const customerRole = this.getCustomerRoleType(partner);

            const legacy = {
                Customer: {
                    wordBinding: `${customerRole.wordBinding}`,
                    Nominativ: {
                        singular: {
                            article: 'der',
                            value: `${customerRole.nominativSingular}`,
                        },
                        plural: {
                            article: 'die',
                            value: `${customerRole.nominativPlural}`,
                        },
                    },
                    Dativ: {
                        singular: {
                            article: `${customerRole.dativSinglularArticle}`,
                            value: `${customerRole.dativSingular}`
                        },
                        plural: {
                            article: 'den',
                            value: `${customerRole.dativPlural}`,
                        },
                    },
                },
                Commission: partnersWithProjects.includes(partner.Id) ? {
                    wordBinding: 'Projekt',
                    Nominativ: {
                        singular: {
                            article: 'das',
                            value: 'Projekt',
                        },
                        plural: {
                            article: 'die',
                            value: 'Projekte',
                        },
                    },
                    Genitiv: {
                        singular: {
                            article: 'des',
                            value: 'Projekts',
                        },
                        plural: {
                            article: 'der',
                            value: 'Projekte',
                        },
                    },
                    Dativ: {
                        singular: {
                            article: 'dem',
                            value: 'Projekt',
                        },
                        plural: {
                            article: 'den',
                            value: 'Projekten',
                        },
                    },
                } : DefaultLabels.Commission,
            }

            const getLabelsBasedOnOtherLabelsIfNotGiven = (fields: Partial<Pick<customLabelsLegacy&CustomLabels, 'Customer' | 'Supplier' | 'CustomerSupplier' | 'CustomerList' | 'CustomerSpecificationType' >>) => {
                return {
                    CustomerSupplier: fields.CustomerSupplier || {
                        wordBinding: `${fields.Customer.wordBinding} / ${fields.Supplier.wordBinding}`,
                        Nominativ: {
                            singular: {
                                article: 'der',
                                value: `${fields.Customer.Nominativ.singular.value} / ${fields.Supplier.Nominativ.singular.value}`,
                            },
                            plural: {
                                article: 'die',
                                value: `${fields.Customer.Nominativ.plural.value} / ${fields.Supplier.Nominativ.plural.value}`,
                            },
                        },
                        Dativ: {
                            singular: {
                                article: `${fields.Customer.Dativ.singular.article}`,
                                value: `${fields.Customer.Dativ.singular.value} / ${fields.Supplier.Dativ.singular.value}`
                            },
                            plural: {
                                article: 'den',
                                value: `${fields.Customer.Dativ.plural.value} / ${fields.Supplier.Dativ.plural.value}`,
                            },
                        },
                    },
                    CustomerList: fields.CustomerList || {
                        wordBinding: `${fields.Customer.wordBinding}- / ${fields.Supplier.wordBinding}liste`,
                        Nominativ: {
                            singular: {
                                article: 'die',
                                value: `${fields.Customer.wordBinding}- / ${fields.Supplier.wordBinding}liste`,
                            },
                            plural: {
                                article: 'die',
                                value: `${fields.Customer.wordBinding}- / ${fields.Supplier.wordBinding}listen`,
                            },
                        },
                    },
                    CustomerSpecificationType: fields.CustomerSpecificationType || {
                        Nominativ: {
                            singular: {
                                article: 'die',
                                value: `${fields.Customer.wordBinding}art`,
                            },
                            plural: {
                                article: 'die',
                                value: `${fields.Customer.wordBinding}arten`,
                            },
                        },
                    },
                }
            }
            /**
             * Defaultwerte werden mit den lagacy werten überschrieben, sobald alle Partner die werte in der Datenbank stehen haben kann das weg
             */
            const defaultOverrides = {
                CommissionFreeTextField1: {
                    Nominativ: {
                        singular: {
                            article: 'die',
                            value: 'Allg. Info zum ' + legacy.Commission.Nominativ.singular.value,
                        },
                        plural: {
                            article: 'die',
                            value: 'Allg. Infos zum ' + legacy.Commission.Nominativ.singular.value,
                        }
                    }
                },
                CommissionCompleteBusinessVolume: {
                    Nominativ: {
                        singular: {
                            article: 'der',
                            value: 'Geplanter Umsatz (Netto)',
                        },
                        plural: {
                            article: 'die',
                            value: 'Geplanten Umsätze (Netto)',
                        }
                    }
                },

                CustomerNumber:  {
                    wordBinding: `${legacy.Customer.wordBinding}nummer`,
                    Nominativ: {
                        singular: {
                            article: 'die',
                            value: `${legacy.Customer.wordBinding}nummer`,
                        },
                        plural: {
                            article: 'die',
                            value: `${legacy.Customer.wordBinding}nummern`,
                        },
                    },
                },
                CustomerNumberShort:  {
                    wordBinding: `${legacy.Customer.wordBinding} Nr.`,
                    Nominativ: {
                        singular: {
                            article: 'die',
                            value: `${legacy.Customer.wordBinding} Nr.`,
                        },
                        plural: {
                            article: 'die',
                            value: `${legacy.Customer.wordBinding} Nr.`,
                        },
                    },
                },
                // default wert wird null gesetzt, damit er ggf von der getLabelsBasedOnOtherLabelsIfNotGiven funktion überschrieben werden kann
                CustomerSpecificationType: null,
                ...legacy,
            }
            /**
             * statische label und custom Label werden zusammengemerged
             */
            const labels = {
                ...staticLabels,
                ...legacy,// werden zwar von "defaultOverrides" wieder überschrieben aber ohne die auch hier hin zu schreiben gibt es einen type fehler
                ...deepMerge({ ...DefaultLabels }, defaultOverrides, partnerCustomLabels),
            }
            /**
             * ggf fehlende Label die sich aus anderen Labeln zusammensetzen werden rein gemerged
             */
            return { ...labels, ...getLabelsBasedOnOtherLabelsIfNotGiven(labels) };
        }),
        shareReplay(1),
    );
    constructor(
        private store: Store<State>,
        private partnerResolver: PartnerResolver
    ) {
    }

    getLabels$(label: keyof customLabelsLegacy | keyof CustomLabels) {
        return this.labels$.pipe(map((labels) => labels[label]));
    }
    getSingle$(label: keyof customLabelsLegacy | keyof CustomLabels, fall: fallType = 'Nominativ'): Observable<string> {
        return this.getLabels$(label).pipe(map((l) => l[fall]?.singular.value));
    }
    getMultiple$(label: keyof customLabelsLegacy | keyof CustomLabels, fall: fallType = 'Nominativ') {
        return this.getLabels$(label).pipe(map((l) => l[fall]?.plural.value));
    }

    private getCustomerRoleType(partner: PartnerEntity) {
        if (partnersWithBauherr.includes(partner.Id)) {
            return {
                wordBinding: 'Bauherren',
                nominativSingular: 'Bauherr',
                nominativPlural: 'Bauherren',
                dativSinglularArticle: 'dem',
                dativSingular: 'Bauherren',
                dativPlural: 'Bauherren',
            };
        }
        if (partner.PartnerSpecificationTypeId === PartnerSpecificationType.Appraiser) {
            return {
            wordBinding: 'Auftraggeber',
                nominativSingular: 'Auftraggeber',
                nominativPlural: 'Auftraggeber',
                dativSinglularArticle: 'dem',
                dativSingular: 'Auftraggeber',
                dativPlural: 'Auftraggebern',
        };
        }
        return {
            wordBinding: DefaultLabels.Customer.wordBinding,
            nominativSingular: DefaultLabels.Customer.Nominativ.singular.value,
            nominativPlural: DefaultLabels.Customer.Nominativ.plural.value,
            dativSinglularArticle: DefaultLabels.Customer.Dativ.singular.article,
            dativSingular: DefaultLabels.Customer.Dativ.singular.value,
            dativPlural: DefaultLabels.Customer.Dativ.plural.value,
        };
    }

    ngOnDestroy() {
        this.destroy$.next();
    }
}
